import { FC } from 'react';

import ModalWithFullScreenBg from 'shared/components/ui/Modal/ModalWithFullScreenBg';

import closeX from 'assets/svg/close-x.svg';

import './rules-modal.scss';

interface IRulesModal {
  open: boolean;
  onClose: () => void;
}

export const RulesModal: FC<IRulesModal> = ({ open, onClose }) => {
  return (
    <ModalWithFullScreenBg
      open={open}
      className="bg-background/60"
      childrenClassName="max-w-[62.5rem] scrollbar-custom"
      withGridBackground={false}
    >
      <div className="relative bg-[#080540] px-2 pb-1 pt-10">
        <button onClick={onClose} className="absolute right-4 top-2">
          <img src={closeX} alt="Close" className="h-6 w-6" />
        </button>

        <h1 className="mb-4 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-center text-xl font-extrabold text-transparent">
          Rock Paper Scissors NFT Race Promotion — Official Rules
        </h1>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg font-extrabold text-transparent">
          1. Promotion Period
        </h2>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          The Rock Paper Scissors NFT Race Promotion ("the Promotion") will run from &nbsp;
          <strong>September 1, 2025, at 00:00 UTC</strong> to &nbsp;
          <strong>September 28, 2025, at 23:59 UTC</strong>.
        </p>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg font-extrabold text-transparent">
          2. Eligibility
        </h2>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          The Promotion is open to all new and existing Rock Paper Scissors players. By
          participating, you agree to these Official Rules.
        </p>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg font-extrabold text-transparent">
          3. How to Participate
        </h2>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          Participation in the Promotion is based on gameplay within the Rock Paper Scissors game.
          Players can earn Bonus Points by completing daily quests. These Bonus Points are the
          primary currency for minting and forging NFTs.
        </p>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg font-extrabold text-transparent">
          4. NFTs and Scoring
        </h2>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          NFTs serve as skins, provide in-game utility, and are the basis for Leaderboard scoring.
          There are seven (7) levels of NFTs:
        </p>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>
            <strong>Bronze:</strong> Minted for 100 Bonus Points.
          </li>
          <li>
            <strong>Silver:</strong> Forged from Bronze NFTs.
          </li>
          <li>
            <strong>Gold:</strong> Forged from Silver NFTs.
          </li>
          <li>
            <strong>Platinum:</strong> Forged from Gold NFTs.
          </li>
          <li>
            <strong>Diamond:</strong> Forged from Platinum NFTs.
          </li>
          <li>
            <strong>Samurai:</strong> Forged from Diamond NFTs.
          </li>
          <li>
            <strong>Grand Master:</strong> Forged from Samurai NFTs.
          </li>
        </ul>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          To get a higher-level NFT, players must forge a lower-level NFT. The success rate of
          forging is determined by the number of lower-level NFTs used. For example, forging four
          (4) Bronze NFTs gives a 40% chance of successfully creating one (1) Silver NFT.
        </p>

        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          Each NFT grants a specific number of points towards your Leaderboard score:
        </p>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>Bronze: 1 point</li>
          <li>Silver: 6 points</li>
          <li>Gold: 35 points</li>
          <li>Platinum: 200 points</li>
          <li>Diamond: 1200 points</li>
          <li>Samurai: 7,000 points</li>
          <li>Grand Master: 40,000 points</li>
        </ul>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          Your total score is the sum of points from all NFTs you own. For example, a player with
          one Platinum, one Silver, and one Bronze hand would have a total score of 200+6+1=207
          points.
        </p>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-xl font-extrabold text-transparent">
          5. Prize Pool &amp; Distribution
        </h2>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          The total prize pool for this Promotion is <strong>$5,000 USDT</strong>, distributed
          across two categories: the Leaderboard and the Prize Box.
        </p>

        <h3 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg text-transparent">
          5.1 Leaderboard Prizes ($3,000 USDT)
        </h3>
        <p className="pl-2 text-[14px] text-white lg:mb-4 lg:text-base">
          The Leaderboard is ranked based on each player's NFT score. The final Leaderboard ranking
          on September 28, 2025, at 23:59 UTC will determine the winners.
        </p>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>
            <strong>Grand Prize:</strong> The player in 1st place on the Leaderboard will receive{' '}
            <strong>$500 USDT</strong>.
          </li>
          <li>
            <strong>Top 100:</strong> Players ranked 2nd through 100th will share a prize pool of{' '}
            <strong>$2,500 USDT</strong>. The specific prize amounts range from{' '}
            <strong>$300 USDT</strong> for 2nd place down to <strong>$10 USDT</strong> for 100th
            place.
          </li>
        </ul>

        <h3 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-lg text-transparent">
          5.2 Prize Box Prizes ($2,000 USDT)
        </h3>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>
            Players can play Rock Paper Scissors against the Prize Box up to three (3) times per
            day, with each attempt costing Bonus Points.
          </li>
          <li>Attempts can be refreshed at a cost of $1 USDT.</li>
          <li>
            The amount of USDT a player can win from the Prize Box increases with the level of their
            NFTs. A number of lucky players will win prizes from this pool.
          </li>
        </ul>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-xl font-extrabold text-transparent">
          6. Winner Announcement and Prize Distribution
        </h2>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>
            All Leaderboard winners will be announced on <strong>October 1, 2025</strong>.
          </li>
          <li>
            All prizes will be distributed to the winning players within one (1) week of the winners
            being announced.
          </li>
        </ul>

        <h2 className="mb-2 bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-xl font-extrabold text-transparent">
          7. General Rules
        </h2>
        <ul className="list-disc pl-6 text-[14px] text-white lg:mb-4 lg:text-base">
          <li>
            The organizer reserves the right to modify these rules or disqualify any player for
            fraudulent or unfair play.
          </li>
          <li>The final interpretation of these rules rests with the Promotion organizers.</li>
        </ul>
      </div>
    </ModalWithFullScreenBg>
  );
};
